/**
 * GWP Progress Bar — Web Component
 * Multi-tier version driven by window.SABAI_TIERS (set from shop metafield
 * sabai_ce_ext.tiered_discount_tiers via theme.liquid).
 *
 * Attribute fallbacks (used when SABAI_TIERS is absent):
 *   threshold        Dollar amount that unlocks GWP. Default: 3500
 *   gwp-variant-id   Shopify variant ID of the gift. Default: 53605055594859
 *   gift-label       Text used in the prompt. Default: "free gift"
 *
 * For Rebuy inline paste, also accepts a `tiers` attribute (JSON string).
 */

(function () {
  let _syncLock = false;

  // 2026-10-02: a reconcile that cannot reach its desired state used to loop
  // forever, and the drawer spinning without ever settling is what that looks
  // like to the shopper.
  //
  // _notifyCart() dispatches cart:refresh and cart:updated, and both are in the
  // list _bindEvents() subscribes to, so every gift add or removal schedules
  // another reconcile. That is deliberate -- the write moves the subtotal and the
  // next pass has to see it -- and it terminates only because the pass after a
  // SUCCESSFUL write finds nothing left to do. When the write cannot achieve what
  // it wanted, nothing ends it: a gift variant that is out of stock, a line
  // Shopify re-splits or re-adds, a tier whose own discount keeps the subtotal
  // moving across the threshold. Each turn of that loop takes Shopify's per-cart
  // lock, so the shopper's quantity step or delete queues behind an unbounded
  // run of gift writes and the Rebuy flyout never repaints.
  //
  // Each add/remove therefore gets a bounded number of attempts. The counter is
  // cleared the moment a pass finds that variant already in the state we want --
  // which is exactly what a successful write looks like one pass later -- so
  // normal operation never approaches the cap.
  const MAX_SYNC_ATTEMPTS = 2;
  const _syncAttempts = new Map();

  function reconcileAllowed(op, variantId) {
    const key = op + ':' + variantId;
    const n = _syncAttempts.get(key) || 0;
    if (n >= MAX_SYNC_ATTEMPTS) {
      if (n === MAX_SYNC_ATTEMPTS) {
        _syncAttempts.set(key, n + 1);   // warn once, not on every cart.change
        console.warn(
          '[gwp-progress-bar] giving up on ' + op + ' of variant ' + variantId +
          ' after ' + MAX_SYNC_ATTEMPTS + ' attempts -- the cart never reached the ' +
          'state the tiers ask for. Check that the gift variant is in stock and ' +
          'that nothing else is rewriting that line (window.__sabaiTraceCart = true).'
        );
      }
      return false;
    }
    _syncAttempts.set(key, n + 1);
    return true;
  }

  // A variant already in the state the tiers want: whatever we last did to it
  // worked, so it starts from a clean slate next time it needs changing.
  function reconcileSettled(variantId) {
    _syncAttempts.delete('add:' + variantId);
    _syncAttempts.delete('rm:' + variantId);
  }

  if (customElements.get('gwp-progress-bar')) return;

  // ── SVG icons ─────────────────────────────────────────────────────────────

  const SVG_BAG = '<svg width="10" height="12" viewBox="0 0 15 18" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12.7808 3.71012H10.9961C10.9961 1.73797 9.39873 0.140625 7.42658 0.140625C5.45444 0.140625 3.85709 1.73797 3.85709 3.71012H2.07234C1.09073 3.71012 0.287598 4.51325 0.287598 5.49486V16.2033C0.287598 17.185 1.09073 17.9881 2.07234 17.9881H12.7808C13.7624 17.9881 14.5656 17.185 14.5656 16.2033V5.49486C14.5656 4.51325 13.7624 3.71012 12.7808 3.71012ZM7.42658 1.92537C8.40819 1.92537 9.21133 2.72851 9.21133 3.71012H5.64184C5.64184 2.72851 6.44497 1.92537 7.42658 1.92537ZM12.7808 16.2033H2.07234V5.49486H3.85709V7.27961C3.85709 7.77042 4.25866 8.17198 4.74946 8.17198C5.24027 8.17198 5.64184 7.77042 5.64184 7.27961V5.49486H9.21133V7.27961C9.21133 7.77042 9.6129 8.17198 10.1037 8.17198C10.5945 8.17198 10.9961 7.77042 10.9961 7.27961V5.49486H12.7808V16.2033Z" fill="currentColor"/></svg>';

  const SVG_CHECK = '<svg width="9" height="8" viewBox="0 0 9 8" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M2.82415 6.15237L0.726419 4.05464L0.012085 4.76395L2.82415 7.57601L8.86078 1.53938L8.15147 0.830078L2.82415 6.15237Z" fill="currentColor"/></svg>';

  // ── Tier helpers ──────────────────────────────────────────────────────────

  function parseTiers(raw) {
    if (!Array.isArray(raw) || !raw.length) return null;
    return raw
      .filter(t => t.minSubtotal > 0)
      .sort((a, b) => a.minSubtotal - b.minSubtotal)
      .map(t => ({
        minCents: Math.round(t.minSubtotal * 100),
        discountAmount: t.discountAmount || 0,
        gwpEnabled: !!t.gwpEnabled,
        gwpVariantIds: (t.gwpItems || []).map(g => {
          const m = String(g.variantId).match(/(\d+)$/);
          return m ? parseInt(m[1], 10) : null;
        }).filter(Boolean),
        stepLabel: buildStepLabel(t),
        benefitText: buildBenefitText(t),
      }));
  }

  function buildStepLabel(t) {
    const parts = [];
    if (t.discountAmount) parts.push('$' + t.discountAmount + ' Discount');
    if (t.gwpEnabled) parts.push('+ free gift');
    return parts.join(' ') || ('$' + t.minSubtotal);
  }

  function buildBenefitText(t) {
    const parts = [];
    if (t.discountAmount) parts.push('$' + t.discountAmount + ' Discount');
    if (t.gwpEnabled) parts.push('a free gift');
    return parts.join(' + ') || 'your reward';
  }

  // ── Component ─────────────────────────────────────────────────────────────

  class GwpProgressBar extends HTMLElement {
    static get observedAttributes() {
      return ['threshold', 'gwp-variant-id', 'gift-label', 'tiers'];
    }

    constructor() {
      super();
      this._handler = null;
      this._tiers = null;
    }

    connectedCallback() {
      this._readConfig();
      if (!this.shadowRoot) this._render();
      this._fetch();
      this._bindEvents();
    }

    disconnectedCallback() {
      this._unbindEvents();
      clearTimeout(this._resyncTimer);
    }

    attributeChangedCallback() {
      if (!this.isConnected) return;
      this._readConfig();
      this._rebuildSteps();
      this._fetch();
    }

    // ─── Config ──────────────────────────────────────────────────────────────

    _readConfig() {
      // Priority: window.SABAI_TIERS → tiers attribute → single threshold
      let raw = window.SABAI_TIERS || null;
      if (!raw) {
        try { raw = JSON.parse(this.getAttribute('tiers') || 'null'); } catch (_) {}
      }

      this._tiers = parseTiers(raw);

      if (this._tiers && this._tiers.length) {
        this._maxCents = this._tiers[this._tiers.length - 1].minCents;
        this._allGwpIds = new Set(this._tiers.flatMap(t => t.gwpVariantIds));
      } else {
        // Single-tier fallback
        this._thresholdCents = Math.round(
          parseFloat(this.getAttribute('threshold') || '3500') * 100
        );
        this._gwpVariantId = parseInt(
          this.getAttribute('gwp-variant-id') || '53605055594859', 10
        );
        this._giftLabel = this.getAttribute('gift-label') || 'free gift';
        this._maxCents = this._thresholdCents;
        this._allGwpIds = new Set([this._gwpVariantId]);
      }
    }

    // ─── Events ──────────────────────────────────────────────────────────────

    _bindEvents() {
      this._handler = this._debounce(() => this._fetch(), 300);
      [
        'cart:refresh', 'cart:updated', 'cart:update',
        'rebuy:cart:change', 'rebuy:cart.change', 'rebuy:cart.updated',
        'theme:cart:updated',
      ].forEach(e => document.addEventListener(e, this._handler));
    }

    _unbindEvents() {
      if (!this._handler) return;
      [
        'cart:refresh', 'cart:updated', 'cart:update',
        'rebuy:cart:change', 'rebuy:cart.change', 'rebuy:cart.updated',
        'theme:cart:updated',
      ].forEach(e => document.removeEventListener(e, this._handler));
    }

    // ─── Cart ────────────────────────────────────────────────────────────────

    // 2026-09-30: two guards, both for the same failure -- a click in the Rebuy
    // drawer that looked like it did nothing.
    //
    // _syncGwp does not just paint: it POSTs to /cart/add.js and /cart/change.js,
    // adding and removing real cart lines. This method is reached from a 300ms
    // debounce on cart.change, so the shopper's NEXT click lands inside that
    // window routinely. Reconciling gifts against a snapshot that a newer write
    // has already superseded meant writing the pre-click cart back out on top of
    // the shopper's own change, and the line list Rebuy had rendered from went
    // stale underneath it.
    //
    //   _fetchSeq   drops a snapshot that a later read has already overtaken.
    //   the barrier  defers the reconcile while any cart write is in flight.
    //
    // Deferring is safe because the write we are waiting on emits its own
    // cart.change, which brings us straight back here with a fresh cart; the
    // retry below is only there for a write that emits nothing.
    async _fetch() {
      const seq = (this._fetchSeq = (this._fetchSeq || 0) + 1);
      try {
        const r = await fetch('/cart.js', {
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
          cache: 'no-store',
        });
        if (!r.ok) return;
        const cart = await r.json();
        if (seq !== this._fetchSeq) return;

        this._updateUI(cart);

        if (this._cartWriteInFlight()) {
          clearTimeout(this._resyncTimer);
          this._resyncTimer = setTimeout(() => this._fetch(), 400);
          return;
        }
        this._syncGwp(cart);
      } catch (e) {
        console.warn('[gwp-progress-bar] fetch failed', e);
      }
    }

    // True while a POST to /cart/* is outstanding, as tracked by the coalescer in
    // snippets/cart-prefetch.liquid. Absent that snippet there is nothing to ask,
    // so this reports false and behaviour is unchanged.
    _cartWriteInFlight() {
      try {
        const api = window.__sabaiCart;
        return !!(api && typeof api.stats === 'function' && api.stats().writesInFlight > 0);
      } catch (e) {
        return false;
      }
    }

    _originalSubtotal(cart) {
      return cart.items
        .filter(i => !this._allGwpIds.has(i.variant_id))
        .reduce((s, i) => s + (i.original_line_price ?? i.line_price), 0);
    }

    // ─── UI ──────────────────────────────────────────────────────────────────

    _updateUI(cart) {
      const subtotal = this._originalSubtotal(cart);
      const pct = Math.min((subtotal / this._maxCents) * 100, 100);
      const root = this.shadowRoot;
      if (!root) return;

      const fill = root.querySelector('.gwp__fill');
      if (fill) fill.style.width = pct + '%';

      if (this._tiers) {
        // Update each milestone icon
        this._tiers.forEach((tier, i) => {
          const step = root.querySelector(`.gwp__step[data-i="${i}"]`);
          if (!step) return;
          const icon = step.querySelector('.gwp__step-icon');
          const done = subtotal >= tier.minCents;
          if (icon) {
            icon.classList.toggle('gwp__step-icon--done', done);
            icon.innerHTML = done ? SVG_CHECK : SVG_BAG;
          }
        });

        // Prompt: show progress toward the next unqualified tier
        const prompt = root.querySelector('.gwp__prompt');
        if (prompt) {
          const next = this._tiers.find(t => subtotal < t.minCents);
          if (next) {
            const rem = next.minCents - subtotal;
            const dollars = fmt(rem);
            prompt.textContent = `You are $${dollars} away from ${next.benefitText}!`;
          } else {
            prompt.textContent = "You've unlocked all your rewards!";
          }
        }
      } else {
        // Single-tier fallback
        const done = pct >= 100;
        const icon = root.querySelector('.gwp__step-icon');
        if (icon) {
          icon.classList.toggle('gwp__step-icon--done', done);
          icon.innerHTML = done ? SVG_CHECK : SVG_BAG;
        }
        const prompt = root.querySelector('.gwp__prompt');
        if (prompt) {
          if (done) {
            prompt.textContent = `You've unlocked your ${this._giftLabel}!`;
          } else {
            const dollars = fmt(this._thresholdCents - subtotal);
            prompt.textContent = `You are $${dollars} away from a ${this._giftLabel}!`;
          }
        }
      }
    }

    // ─── GWP sync ────────────────────────────────────────────────────────────

    async _syncGwp(cart) {
      if (_syncLock) return;
      if (this._tiers) {
        await this._syncTiered(cart);
      } else {
        await this._syncSingle(cart);
      }
    }

    async _syncTiered(cart) {
      const subtotal = this._originalSubtotal(cart);
      const gwpTiers = this._tiers.filter(t => t.gwpEnabled && t.gwpVariantIds.length);
      const allGwpTierIds = new Set(gwpTiers.flatMap(t => t.gwpVariantIds));
      const desiredIds = new Set(
        gwpTiers.filter(t => subtotal >= t.minCents).flatMap(t => t.gwpVariantIds)
      );

      for (const id of allGwpTierIds) {
        const inCart = cart.items.find(i => i.variant_id === id);
        const want = desiredIds.has(id);
        if (want === !!inCart) {
          reconcileSettled(id);
        } else if (want) {
          if (reconcileAllowed('add', id)) await this._cartAdd(id);
        } else {
          if (reconcileAllowed('rm', id)) await this._cartRemove(inCart.key);
        }
      }
    }

    async _syncSingle(cart) {
      const subtotal = this._originalSubtotal(cart);
      const id = this._gwpVariantId;
      const gwpItem = cart.items.find(i => i.variant_id === id);
      const qualified = subtotal >= this._thresholdCents;
      if (qualified === !!gwpItem) {
        reconcileSettled(id);
      } else if (qualified) {
        if (reconcileAllowed('add', id)) await this._cartAdd(id);
      } else {
        if (reconcileAllowed('rm', id)) await this._cartRemove(gwpItem.key);
      }
    }

    async _cartAdd(variantId) {
      _syncLock = true;
      try {
        const r = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: variantId, quantity: 1, properties: { _gwp: '1' } }),
        });
        if (r.ok) this._notifyCart();
      } catch (e) {
        console.warn('[gwp-progress-bar] add failed', e);
      } finally {
        _syncLock = false;
      }
    }

    async _cartRemove(lineKey) {
      _syncLock = true;
      try {
        const r = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: lineKey, quantity: 0 }),
        });
        if (r.ok) this._notifyCart();
      } catch (e) {
        console.warn('[gwp-progress-bar] remove failed', e);
      } finally {
        _syncLock = false;
      }
    }

    _notifyCart() {
      const R = window.Rebuy;
      if (R) {
        if (typeof R.Cart?.reload === 'function') R.Cart.reload();
        else if (typeof R.Cart?.update === 'function') R.Cart.update();
        else if (typeof R.Cart?.refresh === 'function') R.Cart.refresh();
      }
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      document.dispatchEvent(new CustomEvent('cart:updated'));
    }

    // ─── Shadow DOM ──────────────────────────────────────────────────────────

    _stepsHTML() {
      const single = !this._tiers || !this._tiers.length;
      if (single) {
        return `<div class="gwp__step" data-i="0">
          <div class="gwp__step-icon">${SVG_BAG}</div>
          <div class="gwp__step-label">Free Gift</div>
        </div>`;
      }
      return this._tiers.map((tier, i) =>
        `<div class="gwp__step" data-i="${i}">
          <div class="gwp__step-icon">${SVG_BAG}</div>
          <div class="gwp__step-label">${tier.stepLabel}</div>
        </div>`
      ).join('');
    }

    _rebuildSteps() {
      const root = this.shadowRoot;
      if (!root) return;
      const container = root.querySelector('.gwp__steps');
      if (container) {
        container.innerHTML = this._stepsHTML();
        const single = !this._tiers || this._tiers.length <= 1;
        container.classList.toggle('gwp__steps--single', single);
      }
    }

    _render() {
      const single = !this._tiers || this._tiers.length <= 1;
      const shadow = this.attachShadow({ mode: 'open' });
      shadow.innerHTML = `
        <style>
          :host {
            display: block;
            font-family: Topol, -apple-system, BlinkMacSystemFont, 'Helvetica Neue', sans-serif;
            box-sizing: border-box;
            padding: 4px 16px 8px;
          }

          .gwp__wrapper { display: flex; flex-direction: column; gap: 8px; }

          /* Steps row: evenly spaced, first icon at left edge, last at right edge */
          .gwp__steps {
            display: flex;
            justify-content: space-between;
            align-items: flex-end;
          }

          /* Single-step fallback: push the one step to the right end */
          .gwp__steps--single {
            justify-content: flex-end;
          }

          .gwp__step {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 4px;
          }

          /* Anchor label text so first/last don't overflow the bar edges */
          .gwp__step:first-child { align-items: flex-start; }
          .gwp__step:last-child  { align-items: flex-end; }
          /* Keep icon itself centered regardless */
          .gwp__step:first-child .gwp__step-icon,
          .gwp__step:last-child  .gwp__step-icon { align-self: center; }

          .gwp__step-icon {
            width: 18px;
            height: 18px;
            border-radius: 50%;
            background: #EEEEEE;
            display: flex;
            align-items: center;
            justify-content: center;
            color: #8494A0;
            flex-shrink: 0;
            transition: background 0.4s ease, color 0.4s ease;
          }

          .gwp__step-icon--done {
            background: #FF8327;
            color: #FFFFFF;
          }

          .gwp__step-label {
            font-size: 12px;
            font-weight: 300;
            color: rgb(55, 55, 55);
            white-space: nowrap;
          }

          /* Track */
          .gwp__track {
            height: 10px;
            background: #EEEEEE;
            border-radius: 10px;
            overflow: hidden;
          }

          .gwp__fill {
            height: 100%;
            width: 0%;
            background: #FF8327;
            border-radius: 10px;
            transition: width 0.6s cubic-bezier(0.4, 0, 0.2, 1);
          }

          /* Prompt */
          .gwp__prompt {
            font-size: 14px;
            font-weight: 300;
            font-style: normal;
            color: rgb(55, 55, 55);
            text-align: center;
            margin-top: 2px;
            min-height: 17px;
            line-height: 21px;
          }
        </style>

        <div class="gwp__wrapper">
          <div class="gwp__steps${single ? ' gwp__steps--single' : ''}">${this._stepsHTML()}</div>
          <div class="gwp__track">
            <div class="gwp__fill"></div>
          </div>
          <div class="gwp__prompt">Loading&hellip;</div>
        </div>
      `;
    }

    _debounce(fn, ms) {
      let t;
      return () => { clearTimeout(t); t = setTimeout(fn, ms); };
    }
  }

  function fmt(cents) {
    return (Math.max(cents, 0) / 100).toLocaleString('en-US', {
      minimumFractionDigits: 2, maximumFractionDigits: 2,
    });
  }

  customElements.define('gwp-progress-bar', GwpProgressBar);
})();
